import { useEffect, useState } from 'react';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions, auth } from '../../lib/firebase.js';
import { useAuth } from '../../lib/auth.jsx';

// Responders: list, invite (creates Auth user with temp password the admin shares), remove (soft-delete).

export default function Responders() {
  const { claims } = useAuth();
  const vendorId = claims?.vendor_id;

  const [users, setUsers] = useState(null);
  const [showInvite, setShowInvite] = useState(false);
  const [tempPasswordInfo, setTempPasswordInfo] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!vendorId) return;
    return onSnapshot(
      query(collection(db, 'vendors', vendorId, 'users'), where('role', 'in', ['responder', 'vendor_admin'])),
      (snap) => setUsers(snap.docs.map((d) => ({ id: d.id, ...d.data() })).filter((u) => !u.deletedAt)),
      (err) => setError(err.message),
    );
  }, [vendorId]);

  const currentUid = auth.currentUser?.uid;

  async function handleRemove(uid) {
    if (!confirm('Remove this responder? They will lose access immediately.')) return;
    try {
      const remove = httpsCallable(functions, 'removeResponder');
      await remove({ uid });
    } catch (err) {
      setError(err.message);
    }
  }

  async function handleSetRole(uid, role) {
    const label = role === 'vendor_admin' ? 'an Administrator' : 'a Responder';
    if (!confirm(`Make this person ${label}? Their access changes immediately.`)) return;
    try {
      const setRole = httpsCallable(functions, 'setUserRole');
      await setRole({ uid, role });
    } catch (err) {
      setError(err.message);
    }
  }

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <h2>Responders</h2>
        <button onClick={() => setShowInvite(true)} className="btn-primary">+ Invite responder</button>
      </div>
      <p style={{ color: 'var(--muted)', marginTop: -8 }}>
        Technicians, leads, and managers who receive dispatched requests. Add them to teams under <strong>Teams</strong>.
      </p>

      {error && <div className="error">{error}</div>}

      {users === null && <div className="placeholder">Loading…</div>}
      {users && users.length === 0 && (
        <div className="placeholder">No responders yet. Invite your first one.</div>
      )}
      {users && users.length > 0 && (
        <table className="data-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Phone</th>
              <th>Role</th>
              <th>Teams</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id}>
                <td>{u.fullName}</td>
                <td>{u.email}</td>
                <td>
                  {u.phone
                    ? <code>{u.phone}</code>
                    : <span style={{ color: 'var(--muted)' }} title="No phone — voice/SMS fallback can't reach this responder">— none</span>}
                </td>
                <td><span className="badge">{u.role === 'vendor_admin' ? 'Administrator' : u.role === 'stakeholder' ? 'Stakeholder' : 'Responder'}</span></td>
                <td>{(u.teamIds || []).length || <span style={{ color: 'var(--muted)' }}>—</span>}</td>
                <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                  {u.id !== currentUid && (
                    u.role === 'vendor_admin'
                      ? <button onClick={() => handleSetRole(u.id, 'responder')} className="btn-ghost" style={{ marginRight: 6 }}>Make responder</button>
                      : <button onClick={() => handleSetRole(u.id, 'vendor_admin')} className="btn-ghost" style={{ marginRight: 6 }}>Make admin</button>
                  )}
                  {u.role !== 'vendor_admin' && (
                    <button onClick={() => handleRemove(u.id)} className="btn-ghost-danger">Remove</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {showInvite && (
        <InviteModal
          onClose={() => setShowInvite(false)}
          onInvited={(info) => { setShowInvite(false); setTempPasswordInfo(info); }}
        />
      )}
      {tempPasswordInfo && (
        <TempPasswordModal info={tempPasswordInfo} onClose={() => setTempPasswordInfo(null)} />
      )}
    </>
  );
}

function InviteModal({ onClose, onInvited }) {
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [role, setRole] = useState('responder');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  async function submit(e) {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      const invite = httpsCallable(functions, 'inviteResponder');
      const result = await invite({
        email: email.trim(),
        fullName: fullName.trim(),
        role,
        ...(phone.trim() ? { phone: phone.trim() } : {}),
      });
      onInvited({ email: email.trim(), fullName: fullName.trim(), tempPassword: result.data.tempPassword });
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <form className="modal" onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <h3>Invite responder</h3>
        <label>Full name</label>
        <input value={fullName} onChange={(e) => setFullName(e.target.value)} required />
        <label>Email</label>
        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
        <label>Mobile phone (optional but recommended)</label>
        <input
          type="tel"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder="0412 345 678"
          autoComplete="tel"
        />
        <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: -8, marginBottom: 12 }}>
          Needed for SMS + voice-call fallback when push notifications don't reach them.
          AU format (0412 345 678) is fine — we'll normalize it.
        </div>
        <label>Role</label>
        <select value={role} onChange={(e) => setRole(e.target.value)}>
          <option value="responder">Responder</option>
          <option value="vendor_admin">Administrator (can also configure)</option>
          <option value="stakeholder">Stakeholder (notified only, +$3/month)</option>
        </select>
        <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
          <button type="button" onClick={onClose} className="btn-ghost">Cancel</button>
          <button type="submit" disabled={busy} className="btn-primary" style={{ flex: 1 }}>
            {busy ? 'Inviting…' : 'Invite'}
          </button>
        </div>
        {error && <div className="error">{error}</div>}
      </form>
    </div>
  );
}

function TempPasswordModal({ info, onClose }) {
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h3>Share these credentials</h3>
        <p style={{ color: 'var(--muted)' }}>
          {info.fullName} can now sign in with the temp password below. They should reset it after first login.
          You'll see this only once — copy it now.
        </p>
        <div className="kv">
          <div><strong>Email</strong></div>
          <div><code>{info.email}</code></div>
          <div><strong>Temp password</strong></div>
          <div><code>{info.tempPassword}</code></div>
        </div>
        <button onClick={onClose} className="btn-primary" style={{ width: '100%', marginTop: 16 }}>Done</button>
      </div>
    </div>
  );
}
